/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { SuggestUtterancesFilter } from 'src/luis/projects/utterances/components/SuggestUtterancesToolbar';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { LuisId } from 'src/luis/types/Resource';

export class ReviewEndpointUtterancesUIStore {
    public readonly labelingStore = new LabelingStore();

    @observable public readonly inFlightUtteranceIds = new Set<LuisId>();

    @observable public readonly dirtyUtteranceIds = new Set<LuisId>();

    @observable public createNewEntityName: string;

    @observable public isCreateEntityModalOpen = false;

    @observable public toBeDiscardedUtteranceIds: LuisId[] = [];

    @observable public currentFilter: SuggestUtterancesFilter;

    @computed
    public get isDirty() {
        return this.dirtyUtteranceIds.size > 0;
    }

    /**
     * Adds an utterance to the dirty utterances set.
     */
    @action
    public addToDirtyUtterances(ids: LuisId[]) {
        ids.forEach(id => this.dirtyUtteranceIds.add(id));
    }

    /**
     * Removes an utterance to the dirty utterances set.
     */
    @action
    public deleteFromDirtyUtterances(ids: LuisId[]) {
        ids.forEach(id => this.dirtyUtteranceIds.delete(id));
    }

    /**
     * Removes an utterance to the dirty utterances set.
     */
    @action
    public resetDirtyUtterances() {
        this.dirtyUtteranceIds.clear();
    }

    /**
     * Adds an utterance to the in-flight utterances set.
     */
    @action
    public addToInFlightUtterances(ids: LuisId[]) {
        ids.forEach(id => this.inFlightUtteranceIds.add(id));
    }

    /**
     * Removes an utterance to the in-flight utterances set.
     */
    @action
    public deleteFromInFlightUtterances(ids: LuisId[]) {
        ids.forEach(id => this.inFlightUtteranceIds.delete(id));
    }

    /**
     * Sets the deleted utterance id.
     */
    @action
    public setToBeDiscardedUtteranceIds(toBeDiscardedUtteranceIds: LuisId[]) {
        this.toBeDiscardedUtteranceIds = toBeDiscardedUtteranceIds;
    }

    /**
     * Sets the current filter.
     */
    @action
    public setCurrentFilter(filter: SuggestUtterancesFilter) {
        this.currentFilter = filter;
    }

    /**
     * Changes whether the entity modal is open or not.
     */
    @action
    public setIsCreateEntityModalOpen(isOpen: boolean) {
        this.isCreateEntityModalOpen = isOpen;
    }

    /**
     * Changes the entity name based to the entity creation modal.
     */
    @action
    public setCreateNewEntityName(name: string) {
        this.createNewEntityName = name;
    }
}
